import type { Address } from "viem";

import { ACTIVE_CHAIN } from "@/config/chain";
import { AERODROME, VENUES, type VenueId } from "@/config/contracts";

const Node = ({
  label,
  caption,
  href,
  side,
}: {
  label: string;
  caption: string;
  href: string;
  side: "left" | "right";
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`block group shrink-0 ${side === "right" ? "text-right" : ""}`}
  >
    <div
      className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2"
      style={{ color: "var(--ink-4)" }}
    >
      {caption}
    </div>
    <div
      className="font-serif text-[20px] transition-opacity group-hover:opacity-70"
      style={{ fontWeight: 300, letterSpacing: "-0.01em", color: "var(--ink-1)" }}
    >
      {label}
    </div>
  </a>
);

type Props = {
  readonly venue: VenueId;
  readonly proxyAddress?: Address | null;
};

export const RouteDiagram = ({ venue, proxyAddress }: Props) => {
  const lender = VENUES[venue];
  return (
    <section aria-label="Loop route">
    <div className="flex items-center justify-center gap-5 md:gap-8 py-2">
      <Node
        side="left"
        label={lender.shortLabel}
        caption={lender.tagline}
        href={lender.href}
      />

      <div className="flex flex-col items-center gap-1 shrink-0">
        <svg width="48" height="10" viewBox="0 0 48 10" fill="none" aria-hidden>
          <path
            d="M0 5 H40 M34 1 L40 5 L34 9"
            stroke="var(--ink-3)"
            strokeWidth="1"
            strokeLinejoin="round"
            strokeLinecap="round"
            fill="none"
          />
        </svg>
        <svg width="48" height="10" viewBox="0 0 48 10" fill="none" aria-hidden>
          <path
            d="M48 5 H8 M14 1 L8 5 L14 9"
            stroke="var(--ink-3)"
            strokeWidth="1"
            strokeLinejoin="round"
            strokeLinecap="round"
            fill="none"
          />
        </svg>
      </div>

      <Node
        side="right"
        label="Aerodrome"
        caption="USDC → CADC"
        href={AERODROME.href}
      />
    </div>
    {proxyAddress && (
      <div
        className="mt-3 text-center font-mono text-[10px] tracking-[0.06em]"
        style={{ color: "var(--ink-4)" }}
      >
        <span className="uppercase tracking-[0.14em] mr-2">Proxy Wallet</span>
        <a
          href={`${ACTIVE_CHAIN.blockExplorers?.default.url}/address/${proxyAddress}`}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline"
          style={{ color: "var(--ink-3)" }}
        >
          {proxyAddress.slice(0, 10)}…{proxyAddress.slice(-8)}
        </a>
      </div>
    )}
    </section>
  );
};
